import { useCallback, useEffect, useRef, useState } from "react";
import { createPcmPlayer, getWsBase } from "../lib/audio";

const ROOM = "DEMO01";

export function useListener() {
  const [phase, setPhase] = useState("idle"); // idle | connecting | listening | error
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [directionLabel, setDirectionLabel] = useState("🌐 → English");
  const [listeners, setListeners] = useState(0);
  const [audioActive, setAudioActive] = useState(false);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [transcriptPartial, setTranscriptPartial] = useState("");

  const wsRef = useRef(null);
  const playerRef = useRef(null);
  const phaseRef = useRef(phase);
  const mutedRef = useRef(muted);
  const audioTimerRef = useRef(null);
  const reconnectRef = useRef(null);
  phaseRef.current = phase;
  mutedRef.current = muted;

  const cleanup = useCallback(() => {
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    if (audioTimerRef.current) { clearTimeout(audioTimerRef.current); audioTimerRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} wsRef.current = null; }
    if (playerRef.current) { playerRef.current.close(); playerRef.current = null; }
    setAudioActive(false);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const connectWs = useCallback(() => {
    const ws = new WebSocket(`${getWsBase()}/api/ws/listener/${ROOM}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (phaseRef.current === "connecting") setPhase("listening");
    };

    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      switch (msg.type) {
        case "status":
          setLive(msg.live);
          setDirectionLabel(msg.direction_label);
          setListeners(msg.listeners);
          break;
        case "audio":
          if (playerRef.current) {
            playerRef.current.push(msg.data);
            setAudioActive(true);
            if (audioTimerRef.current) clearTimeout(audioTimerRef.current);
            audioTimerRef.current = setTimeout(() => setAudioActive(false), 600);
          }
          break;
        case "translation_delta":
          setTranscriptPartial((p) => p + msg.delta);
          break;
        case "translation_partial":
          setTranscriptPartial(msg.text);
          break;
        case "translation_done":
          setTranscriptLines((l) => [...l, msg.text]);
          setTranscriptPartial("");
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      // auto-reconnect while user is still in listening mode
      if (phaseRef.current === "listening" || phaseRef.current === "connecting") {
        reconnectRef.current = setTimeout(() => {
          if (phaseRef.current === "listening" || phaseRef.current === "connecting") connectWs();
        }, 1500);
      }
    };

    ws.onerror = () => {
      if (phaseRef.current === "connecting") {
        setError("Sunucu bağlantısı kurulamadı.");
        setPhase("error");
      }
    };
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setPhase("connecting");
    try {
      // AudioContext must be created inside the user gesture (iOS Safari)
      const player = createPcmPlayer();
      await player.resume();
      player.setMuted(mutedRef.current);
      playerRef.current = player;
      connectWs();
    } catch (err) {
      setError("Ses başlatılamadı: " + String(err));
      setPhase("error");
    }
  }, [connectWs]);

  const stop = useCallback(() => {
    setPhase("idle");
    cleanup();
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (playerRef.current) playerRef.current.setMuted(next);
      return next;
    });
  }, []);

  return {
    phase, error, live, muted, directionLabel, listeners, audioActive,
    transcriptLines, transcriptPartial, start, stop, toggleMute,
  };
}
